import { getIn } from 'formik';
import React, { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { Expandable } from '../../../../shared/Expandable';
import { FieldErrorIndicator } from '../../../../shared/form/FieldErrorIndicator';
import { FormContext } from '../../../../shared/form/Form';
import { FormFieldRow } from '../../../../shared/form/FormFieldRow';
import { VerticalCheckboxField } from '../../../../shared/form/inputs/CheckboxField';
import { getNullSelectOption, SelectOptions } from '../../../../shared/form/inputs/Select';
import { SelectField } from '../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../shared/form/inputs/TextInputField';
import { Header4 } from '../../../../shared/Headers';
import { colourNeutral09, colourNeutral10 } from '../../../../styling/colours';
import { spacing03, spacing04, spacing05 } from '../../../../styling/spacing';
import { FixingKitTypeCode, fixingKitTypeCodeOptions } from '../../solar/fixingKitTypeCode';
import { DeleteButton } from '../DeleteAndDuplicateButtons';
import { DeleteConfirmation } from '../DeleteConfirmation';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { QuoteOrDesignFormExpandControlChevron } from '../QuoteOrDesignFormExpandControlChevron';
import { solarFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { SectionHeader } from '../SectionHeader';
import { SolarFormModel } from './solarFormModel';

type Props = {
  solar: SolarFormModel;
  unitIndex: number;
  isExpanded: boolean;
  deleteSolar: () => void;
};

export const SolarFields = (props: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue, errors, touched } = formContext.formProps;

  const { unitIndex, isExpanded, deleteSolar } = props;

  const isInReadOnlyMode = useContext(FormContext).isDisabled;

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const solarBaseFieldName = `units.${unitIndex}.solar`;
  const hasNestedError = getIn(errors, solarBaseFieldName) && getIn(touched, solarBaseFieldName);
  const fixingKitType = formContext.formProps.values.units[unitIndex].solar?.fixingKitTypeCode;

  return (
    <Container>
      <SectionHeader>
        {hasNestedError && (
          <NestedErrorsIndicator
            fieldName={`units.${unitIndex}.solar`}
            showError={hasNestedError}
            error="Some fields are invalid"
          />
        )}
        <Header4>Solar</Header4>
        <QuoteOrDesignFormExpandControlChevron
          onClick={() => {
            setFieldValue(solarFieldName(unitIndex, 'isExpanded'), !isExpanded);
          }}
          isExpanded={isExpanded}
        />
        {!isInReadOnlyMode && (
          <DeleteButton onClick={() => setIsConfirmingDelete(true)} testId={solarDeleteButtonTestId(unitIndex)} />
        )}
      </SectionHeader>
      <DeleteConfirmation
        isConfirmingDelete={isConfirmingDelete}
        setIsConfirmingDelete={setIsConfirmingDelete}
        deleteItem={deleteSolar}
        deleteButtonTestId={solarConfirmDeleteButtonTestId(unitIndex)}
      />
      <Expandable isExpanded={isExpanded}>
        <FormFieldRow>
          <SelectField<FixingKitTypeCode | null>
            name={solarFieldName(unitIndex, 'fixingKitTypeCode')}
            options={fixingKitTypeCodeOptions}
            label="Fixing Kit Type"
          />
          <TextInputField
            name={solarFieldName(unitIndex, 'assumedDomesticHotWaterDemandInLitresPerDay')}
            label="Assumed DHW Demand (l/day)"
          />
          <SelectField<1 | 2 | null>
            name={solarFieldName(unitIndex, 'solarPanelQuantity')}
            label="Solar Panel Quantity"
            options={
              fixingKitType === 'InRoofSlate' || fixingKitType === 'InRoofTile'
                ? solarPanelQuantityInRoofFixingKitSelectOptions
                : solarPanelQuantitySelectOptions
            }
          />
          <VerticalCheckboxField
            name={solarFieldName(unitIndex, 'includingHotWaterCylinder')}
            label="Including Hot Water Cylinder"
          />
          <TextInputField
            name={solarFieldName(unitIndex, 'solarInputInKilowattHoursPerYear')}
            label="Solar Input (kWh/year)"
          />
          <TextInputField name={solarFieldName(unitIndex, 'solarFractionPercentage')} label="Solar Fraction (%)" />
        </FormFieldRow>
      </Expandable>
    </Container>
  );
};

const solarPanelQuantitySelectOptions: SelectOptions<1 | 2 | null> = [
  getNullSelectOption<1 | 2>(),
  { value: 1, text: '1 Panel' },
  { value: 2, text: '2 Panels' },
];

const solarPanelQuantityInRoofFixingKitSelectOptions: SelectOptions<2 | null> = [
  getNullSelectOption<2>(),
  { value: 2, text: '2 Panels' },
];

export const solarDeleteButtonTestId = (unitIndex: number) => `unit-${unitIndex}-solar-delete-button`;

export const solarConfirmDeleteButtonTestId = (unitIndex: number) => `unit-${unitIndex}-solar-confirm-delete-button`;

const NestedErrorsIndicator = styled(FieldErrorIndicator)`
  margin-right: ${spacing03};
`;

const Container = styled.div`
  background: ${colourNeutral10};
  margin-top: ${spacing05};
  padding: ${spacing04};
  border: solid 2px ${colourNeutral09};
  position: relative;
`;
